import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme, type ThemeMode } from '../hooks/useTheme';

export function ThemeToggle() {
  const { theme, resolvedTheme, setTheme } = useTheme();

  const themeOptions: { value: ThemeMode; label: string; icon: typeof Sun; description: string }[] = [
    {
      value: 'light',
      label: 'Light',
      icon: Sun,
      description: 'Always use light theme',
    },
    {
      value: 'dark',
      label: 'Dark',
      icon: Moon,
      description: 'Always use dark theme',
    },
    {
      value: 'system',
      label: 'System',
      icon: Monitor,
      description: 'Follow system preference',
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          Theme Preference
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Choose how Chromis should appear
          {theme === 'system' && (
            <span className="ml-1">
              (currently using <strong>{resolvedTheme}</strong> mode)
            </span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {themeOptions.map((option) => {
          const Icon = option.icon;
          const isActive = theme === option.value;

          return (
            <button
              key={option.value}
              onClick={() => setTheme(option.value)}
              className={`
                relative p-4 rounded-lg border-2 transition-all duration-200
                ${
                  isActive
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                }
              `}
              aria-pressed={isActive}
              aria-label={`${option.label} theme`}
            >
              <div className="flex flex-col items-center gap-2">
                <div
                  className={`
                    p-3 rounded-full transition-colors
                    ${
                      isActive
                        ? 'bg-blue-100 dark:bg-blue-800/30 text-blue-600 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }
                  `}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div className="text-center">
                  <div
                    className={`
                      font-semibold
                      ${
                        isActive
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-900 dark:text-gray-100'
                      }
                    `}
                  >
                    {option.label}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {option.description}
                  </div>
                </div>
              </div>
              {isActive && (
                <div className="absolute top-2 right-2">
                  <div className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
          About Theme Settings
        </h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>• Your preference is saved and persists across sessions</li>
          <li>• System mode automatically adapts to your OS theme</li>
          <li>• All color tools work seamlessly in both light and dark modes</li>
        </ul>
      </div>
    </div>
  );
}
